import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const Clients = () => {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    notes: '',
  });

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('sitterId', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching clients:', error);
        alert('Failed to load clients');
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [currentUser]);

  const openModal = (client = null) => {
    // Lock body scroll when modal opens
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    if (client) {
      setEditingClient(client);
      // Handle both old format (name) and new format (firstName/lastName)
      const firstName = client.firstName || client.name?.split(' ')[0] || '';
      const lastName = client.lastName || client.name?.split(' ').slice(1).join(' ') || '';
      
      setFormData({
        firstName,
        lastName,
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        emergencyContact: client.emergencyContact || '',
        emergencyPhone: client.emergencyPhone || '',
        notes: client.notes || '',
      });
    } else {
      setEditingClient(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        emergencyContact: '',
        emergencyPhone: '',
        notes: '',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    // Unlock body scroll when modal closes
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    
    setShowModal(false);
    setEditingClient(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
      notes: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      alert('You must be logged in');
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      alert('Please enter both first and last name');
      return;
    }

    try {
      const clientData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`, // Keep full name for compatibility
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        emergencyContact: formData.emergencyContact.trim(),
        emergencyPhone: formData.emergencyPhone.trim(),
        notes: formData.notes.trim(),
        sitterId: currentUser.uid,
      };

      if (editingClient) {
        // Update existing client
        const clientRef = doc(db, 'clients', editingClient.id);
        await updateDoc(clientRef, {
          ...clientData,
          updatedAt: new Date().toISOString(),
        });
        
        setClients(clients.map(c => 
          c.id === editingClient.id 
            ? { ...c, ...clientData }
            : c
        ));
        alert('Client updated successfully!');
      } else {
        // Add new client
        const clientRef = collection(db, 'clients');
        const docRef = await addDoc(clientRef, {
          ...clientData,
          createdAt: new Date().toISOString(),
        });
        
        setClients([...clients, { id: docRef.id, ...clientData }]);
        alert('Client added successfully!');
      }

      closeModal();
    } catch (error) {
      console.error('Error saving client:', error);
      alert(`Failed to save client: ${error.message}`);
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      const clientRef = doc(db, 'clients', clientId);
      await deleteDoc(clientRef);
      setClients(clients.filter(c => c.id !== clientId));
      alert('Client deleted successfully!');
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pastel-purple"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-brand-brown">Clients</h1>
            <p className="text-brand-brown/60">Manage your client contacts</p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Client</span>
          </button>
        </div>

        {/* Clients Grid */}
        {clients.length === 0 ? (
          <div className="card p-12 text-center">
            <p className="text-brand-brown/60 mb-4">No clients yet</p>
            <button
              onClick={() => openModal()}
              className="btn-primary mx-auto"
            >
              Add Your First Client
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map(client => (
              <div key={client.id} className="card p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-brand-brown">
                      {client.name || `${client.firstName} ${client.lastName}`}
                    </h3>
                    {client.email && (
                      <p className="text-sm text-brand-brown/70">{client.email}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openModal(client)}
                      className="p-2 rounded-lg hover:bg-pastel-blue/20 text-pastel-blue"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteClient(client.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {client.phone && (
                    <div>
                      <span className="font-medium text-brand-brown/70">Phone:</span>
                      <span className="ml-2 text-brand-brown">{client.phone}</span>
                    </div>
                  )}
                  {client.address && (
                    <div>
                      <span className="font-medium text-brand-brown/70">Address:</span>
                      <p className="text-brand-brown mt-1">{client.address}</p>
                    </div>
                  )}
                  {client.emergencyContact && (
                    <div className="pt-2 border-t border-brand-brown/10">
                      <span className="font-medium text-brand-brown/70">Emergency:</span>
                      <p className="text-brand-brown">{client.emergencyContact}</p>
                      {client.emergencyPhone && (
                        <p className="text-brand-brown/70">{client.emergencyPhone}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            style={{ overflow: 'hidden' }}
          >
            <div 
              className="bg-white rounded-2xl max-w-2xl w-full my-8 flex flex-col"
              style={{ maxHeight: '90vh' }}
            >
              <div className="p-6 border-b border-brand-brown/10 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-brand-brown">
                    {editingClient ? 'Edit Client' : 'Add New Client'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Name Fields - Side by Side */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="John"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-brown mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Smith"
                      className="input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, City, State 12345"
                    rows="2"
                    className="textarea"
                  />
                </div>

                <div className="border-t border-brand-brown/10 pt-4">
                  <h3 className="font-semibold text-brand-brown mb-3">Emergency Contact</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-brown mb-1">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        value={formData.emergencyContact}
                        onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                        placeholder="Jane Doe"
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-brown mb-1">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.emergencyPhone}
                        onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                        placeholder="(555) 987-6543"
                        className="input"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Gate code, parking instructions, etc..."
                    rows="3"
                    className="textarea"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-primary flex items-center justify-center space-x-2"
                  >
                    <Save className="h-5 w-5" />
                    <span>{editingClient ? 'Update' : 'Add'} Client</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Clients;
